/**
 * Analytics Redux Slice
 * State + reducers for analytics tracking
 */

import { createSlice, type PayloadAction } from '@ufc/core'
import type {
  PageViewPayload,
  TrackEventPayload,
  IdentifyPayload,
  ErrorPayload,
  PerformancePayload,
} from '../domain/types'
import { generateSessionId } from '../domain/utils'

// ============================================
// State Interface
// ============================================

export interface AnalyticsState {
  /** Unique session identifier */
  sessionId: string
  /** Whether analytics has been initialized */
  isInitialized: boolean
  /** Last tracked page view path */
  lastPageView: string | null
  /** Timestamp when current page view started */
  pageViewStartTime: number
  /** Currently identified user ID */
  userId: string | null
  /** Total events tracked this session */
  eventCount: number
  /** Total errors tracked this session */
  errorCount: number
}

// ============================================
// Initial State
// ============================================

const initialState: AnalyticsState = {
  sessionId: generateSessionId(),
  isInitialized: false,
  lastPageView: null,
  pageViewStartTime: Date.now(),
  userId: null,
  eventCount: 0,
  errorCount: 0,
}

// ============================================
// Slice
// ============================================

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    // ==========================================
    // Lifecycle Actions
    // ==========================================

    /** Initialize analytics (called on app start) */
    initialize: (state) => {
      state.isInitialized = true
      state.pageViewStartTime = Date.now()
    },

    /** Reset analytics state (e.g., on logout) */
    reset: (state) => {
      state.sessionId = generateSessionId()
      state.userId = null
      state.lastPageView = null
      state.pageViewStartTime = Date.now()
      state.eventCount = 0
      state.errorCount = 0
    },

    // ==========================================
    // Tracking Actions (handled by saga)
    // ==========================================

    /**
     * Track page view
     * Saga intercepts and sends to adapter
     */
    trackPageView: (state, action: PayloadAction<PageViewPayload>) => {
      const previousPath = state.lastPageView
      state.lastPageView = action.payload.pagePath
      state.pageViewStartTime = Date.now()
      state.eventCount += 1

      // Payload is passed to saga with previous page duration
      // calculated from state.pageViewStartTime
    },

    /**
     * Track custom event
     * Saga intercepts and sends to adapter
     */
    trackEvent: (state, _action: PayloadAction<TrackEventPayload>) => {
      state.eventCount += 1
    },

    /**
     * Identify user
     * Saga intercepts and sends to adapter
     */
    identify: (state, action: PayloadAction<IdentifyPayload>) => {
      state.userId = action.payload.userId
    },

    /**
     * Track error
     * Saga intercepts and sends to adapter
     * Errors are tracked even without consent
     */
    trackError: (state, _action: PayloadAction<ErrorPayload>) => {
      state.errorCount += 1
    },

    /**
     * Track performance metric
     * Saga intercepts and sends to adapter
     */
    trackPerformance: (state, _action: PayloadAction<PerformancePayload>) => {
      // Performance events don't update state
      // They're just passed through to the saga
    },

    // ==========================================
    // Internal State Updates
    // ==========================================

    /** Update page view start time */
    setPageViewStartTime: (state, action: PayloadAction<number>) => {
      state.pageViewStartTime = action.payload
    },

    /** Regenerate session ID */
    regenerateSession: (state) => {
      state.sessionId = generateSessionId()
    },
  },
})

// ============================================
// Exports
// ============================================

export const analyticsActions = analyticsSlice.actions
export const analyticsReducer = analyticsSlice.reducer
